import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback
} from 'react';

import * as faceapi from 'face-api.js';
import { FaceMesh } from '@mediapipe/face_mesh';
import { Camera } from '@mediapipe/camera_utils';
import { useFRcamera } from '../../useAiCamera';

const FaceDetectionContext = createContext({
  webcamRef: null,
  canvasRef: null,
  loadingModels: false,
  faceDetected: false,
  allowCapture: false,
  capturedImage: null,
  capture: () => {}
});

function FaceDetectionProvider({ children }) {
  const {
    processFace,
    settings: { landmark = false, box = false }
  } = useFRcamera();
  const webcamRef = useRef(null);
  const canvasRef = useRef(null);

  const faceMeshRef = useRef(null);
  const cameraRef = useRef(null);
  const [loadingModels, setLoadingModels] = useState(false);
  const [faceDetected, setFaceDetected] = useState(false);
  const [allowCapture, setAllowCapture] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);

  // =========================
  // LOAD MODELS
  // =========================
  const loadModels = async () => {
    try {
      setLoadingModels(true);

      const MODEL_URL = `${window.location.origin}/models`;

      await Promise.all([
        faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
        faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL)
      ]);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingModels(false);
    }
  };

  // =========================
  // CLEAR CANVAS
  // =========================
  const clearCanvas = () => {
    const canvas = canvasRef.current;

    if (!canvas) return;

    const ctx = canvas.getContext('2d');

    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  // =========================
  // DRAW FACE BOX
  // =========================
  const drawFaceBox = (video, detection) => {
    const canvas = canvasRef.current;

    if (!canvas || !detection) return;

    const ctx = canvas.getContext('2d');

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const box = detection.detection.box;

    ctx.strokeStyle = '#00ff00';
    ctx.lineWidth = 2;

    ctx.strokeRect(box.x, box.y, box.width, box.height);

    ctx.fillStyle = '#00ff00';
    ctx.font = '18px Arial';

    ctx.fillText('Face Detected', box.x, box.y - 10);
  };

  // =========================
  // DRAW LANDMARKS
  // =========================
  const drawLandmarks = results => {
    const canvas = canvasRef.current;

    if (!canvas) return;

    const ctx = canvas.getContext('2d');

    canvas.width = results.image.width;
    canvas.height = results.image.height;

    canvas.style.width = '100%';
    canvas.style.height = '100%';

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (!results.multiFaceLandmarks) return;

    ctx.fillStyle = '#ffffff';

    for (const landmarks of results.multiFaceLandmarks) {
      landmarks.forEach(point => {
        ctx.beginPath();

        ctx.arc(
          point.x * canvas.width,
          point.y * canvas.height,
          1,
          0,
          2 * Math.PI
        );

        ctx.fill();
      });
    }
  };

  // =========================
  // DETECT FACE
  // =========================
  const detectFace = async video => {
    const detection = await faceapi
      .detectSingleFace(video, new faceapi.TinyFaceDetectorOptions())
      .withFaceLandmarks();

    return detection;
  };

  // =========================
  // INIT FACEMESH
  // =========================
  const initFaceMesh = () => {
    const video = webcamRef.current?.video;

    if (!video) return;

    const faceMesh = new FaceMesh({
      locateFile: file =>
        `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`
    });

    faceMesh.setOptions({
      maxNumFaces: 1,
      refineLandmarks: true,
      minDetectionConfidence: 0.5,
      minTrackingConfidence: 0.5
    });

    faceMesh.onResults(async results => {
      if (!results.multiFaceLandmarks?.length) {
        setFaceDetected(false);
        setAllowCapture(false);

        clearCanvas();

        return;
      }

      const detection = await detectFace(video);

      if (!detection) {
        setFaceDetected(false);
        setAllowCapture(false);

        clearCanvas();

        return;
      }

      setFaceDetected(true);
      setAllowCapture(true);

      clearCanvas();

      if (box) {
        drawFaceBox(video, detection);
      }

      if (landmark) {
        drawLandmarks(results);
      }
    });

    faceMeshRef.current = faceMesh;

    const camera = new Camera(video, {
      onFrame: async () => {
        await faceMesh.send({ image: video });
      },
      width: 640,
      height: 480
    });

    camera.start();

    cameraRef.current = camera;
  };

  // =========================
  // CAPTURE
  // =========================
  const capture = useCallback(() => {
    if (!allowCapture) return;

    const image = webcamRef.current?.getScreenshot();
    if (!image) return;

    processFace(image);
    setCapturedImage(image);
  }, [allowCapture]);

  // =========================
  // WAIT FOR VIDEO
  // =========================
  const waitForVideo = () =>
    new Promise(resolve => {
      const check = () => {
        const video = webcamRef.current?.video;

        if (video && video.readyState === 4 && video.videoWidth > 0) {
          resolve(video);
        } else {
          setTimeout(check, 300);
        }
      };

      check();
    });

  // =========================
  // INIT
  // =========================
  useEffect(() => {
    let mounted = true;

    const init = async () => {
      await loadModels();

      if (!mounted) return;

      await waitForVideo();

      if (!mounted) return;

      initFaceMesh();
    };

    init();

    return () => {
      mounted = false;

      if (cameraRef.current) {
        cameraRef.current.stop();
      }
    };
  }, []);

  return (
    <FaceDetectionContext.Provider
      value={{
        webcamRef,
        canvasRef,
        loadingModels,
        faceDetected,
        allowCapture,
        capturedImage,
        capture
      }}
    >
      {children}
    </FaceDetectionContext.Provider>
  );
}

export const useFaceDetection = () => {
  const ctx = useContext(FaceDetectionContext);

  if (!ctx) {
    throw new Error(
      'useFaceDetection must be used within FaceDetectionProvider'
    );
  }

  return ctx;
};

export default FaceDetectionProvider;
